import React, { useState, useEffect, useImperativeHandle, useRef, useCallback } from 'react';
import { equals } from './equals';
import * as patch from './PatchFunctions';


export const FormContext = React.createContext({
    fields: {},
 });

export const Form = React.forwardRef(({
    className = "",
    id = "default-id",
    getList,
    initial = null,
    onFieldChange = null,
    disabled,
    readOnly,
    fallback,
    prompt = { function: () => null, message: "" },
    children,
}, ref) => {

    const [ fields, setFields ] = useState(initial);
    let newFields = fields;

    const fieldsRef = useRef();
    fieldsRef.current = fields;

    const initialRef = useRef();
    initialRef.current = initial;

    useEffect(() => {
        setFields(initial);
    }, [initial])

    const isDirty = !equals(initial, fields);
    prompt.function(prompt.msg, () => !equals(initialRef.current, fieldsRef.current));

    //
    // These are the calls that an application using a reference for the form
    //  can access to make changes.  When they are called normally, the setFields
    //  function will take effect and rerender the form.  When they are called 
    //  inside of an onChange callback, the setFields call will not work as 
    //  expected since the function hasn't completed execution and any setFields
    //  invoked here will be lost.  Instead we save the same information to a
    //  temporary callbackFields variable that is merged into the rest of the 
    //  setFields call inside of the onChange routine.
    //
    let callbackChanges = [];
    useImperativeHandle(ref, () => ({
        onChangeSetVal (fieldName, fieldValue) {
            //setFields({ ...fields, [fieldName]: fieldValue, ...callbackFields });
            //callbackFields = { ...callbackFields, [fieldName]: fieldValue };
            callbackChanges.push({ path: fieldName, value: fieldValue });
        },
        setVal(path, value) {
            //console.log(`setFormVal`, fieldsRef.current, path, value);
            fieldsRef.current = patch.setPath(fieldsRef.current, path, value);
            //console.log(fieldsRef.current);
            if (onFieldChange) {
                const isCurrentlyDirty = !equals(initial, fieldsRef.current)
                onFieldChange(fieldsRef.current, isCurrentlyDirty);
            }
            setFields(fieldsRef.current);
        },
        getAll () {
            return({...fields/*, ...callbackFields*/});
        },
        isDirty () {
            return(isDirty);
        },
        setAll (replacementFields) {
            setFields(replacementFields);
            //callbackFields = replacementFields;
        }
    }));


    //
    // This is the default onChange handler routine that all of our children
    //  form elements will use.  For elements that already have an onChange handler,
    //  that function will be called after this one is invoked
    //
    const defaultOnChange = (event, onChange) => {
        const { target: { name, value, type, checked } } = event;
        //console.log(`DEFAULT onChange[${type}] name=${name} value=${value} (${typeof value}) checked=${checked}`);
        
        //
        // Some specific field types need a little extra processing
        //
        let newValue = value;
        if (type === "number") {
            newValue = parseFloat(value);
        }
        if (type === "checkbox") {
            newValue = checked;
        }
        if (type === "select-multiple") {
            newValue = Object.values(event.target.options).filter((x) => x.selected).map((x) => x.value)
        }

        //
        // Need to call original onChange handler for this field if one was provided.  We need
        //  to be careful while we're back in application code space since the app can make 
        //  imperative calls that will change state, but not in this context (so we need the 
        //  callbackFields helper variable so we can persist those changes after we return).
        //
        if (onChange) {
            onChange(event);
        }

        //
        // Merge any changes from the default handler and any additional setVal type calls made 
        //  during the application chained onChange handler before committing to our internal state
        //
        //let updatedFields = patch.mergeDeep(patch.setPath(newFields, name, newValue), callbackFields);
        let updatedFields = patch.setPath(newFields, name, newValue);
        callbackChanges.forEach(item => {
            updatedFields = patch.setPath(updatedFields, item.path, item.value);
        });
        
        //
        // If the form has requested notification whenever a field is modified this
        //  callback will provide the full set of modified fields to the application
        //
        if (onFieldChange) {
            const isCurrentlyDirty = !equals(initial, updatedFields)
            onFieldChange(updatedFields, isCurrentlyDirty);
        }

        //
        // Again, the application may have made additional callbackField changes so we merge 
        //  everything together again before we commit the state
        //
        //updatedFields = patch.mergeDeep(patch.setPath(newFields, name, newValue), callbackFields);
        updatedFields = patch.setPath(newFields, name, newValue);
        callbackChanges.forEach(item => {
            updatedFields = patch.setPath(updatedFields, item.path, item.value);
        });

        //console.log(fields, updatedFields, callbackFields)
        setFields(updatedFields);
    }

    const defaultReset = (onReset) => {
        setFields(initial);

        if(onReset) {
            onReset(newFields);
        }

        //
        // If the form has requested notification whenever a field is modified this
        //  callback will provide the full set of modified fields to the application
        //  which are being reset to their initial values unless the application
        //  does something to set callbackField values during the onReset callback
        //
        if (onFieldChange) {
            let mergedFields = initial;
            mergedFields = newFields;
            callbackChanges.forEach(item => {
                mergedFields = patch.setPath(mergedFields, item.path, item.value);
            });
                
            const isCurrentlyDirty = !equals(initial, mergedFields)
            onFieldChange(mergedFields, isCurrentlyDirty);
        }
    }


    return(
        <div id={id} className={`form-helper ${className} ${isDirty ? "is-dirty" : ""}`}>
            <FormContext.Provider value={{
                initial,
                current: fields,
                formId: id,
                formGetList: getList,
                formDisabled: disabled,
                formReadOnly: readOnly,
                formIsDirty: isDirty,
                formFallback: fallback,
                defaultOnChange,
                defaultReset,

                onChangeSetVal: (path, value) => {
                    //fieldsRef.current = patch.setPath(fieldsRef.current, path, value)
                    callbackChanges.push({ path, value });
                    //console.log(`onChangeSetVal ${path}`, callbackChanges);
                },
                onChangeRemoveVal: (path) => {
                    //fieldsRef.current = patch.removePath(fieldsRef.current, path)
                    //console.log(`onChangeRemoveVal ${path}`, fields, fieldsRef.current);
                },
                setFormVal: useCallback((path, value) => {
                    //console.log(`setFormVal`, fieldsRef.current, path, value);
                    fieldsRef.current = patch.setPath(fieldsRef.current, path, value);
                    //console.log(fieldsRef.current);
                    if (onFieldChange) {
                        const isCurrentlyDirty = !equals(initial, fieldsRef.current)
                        onFieldChange(fieldsRef.current, isCurrentlyDirty);
                    }
                    setFields(fieldsRef.current);
                }, [ initial, onFieldChange ]),
                removeFormVal: useCallback((path) => { 
                    //console.log(`removeFormVal`, fields, temp);
                    fieldsRef.current = patch.removePath(fieldsRef.current, path); 
                    //console.log(fieldsRef.current);
                    if (onFieldChange) {
                        const isCurrentlyDirty = !equals(initial, fieldsRef.current)
                        onFieldChange(fieldsRef.current, isCurrentlyDirty);
                    }
                    setFields(fieldsRef.current);
                }, [ initial, onFieldChange ]),
            }}>
                {children}
            </FormContext.Provider>
        </div>
    );
});

